import React from 'react';
import { Card, Flex, Grid, Skeleton, Typography, theme } from 'antd';
import { tintColor } from '@/utils/colorTint';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  icon: React.ReactNode;
  /** Accent colour for the value and icon tile; defaults to the primary colour. */
  color?: string;
  /** Shown after the value in secondary text, e.g. "/ 120". */
  suffix?: React.ReactNode;
  /** Small secondary line under the value, e.g. a last-sync time. */
  hint?: React.ReactNode;
  loading?: boolean;
  /** Makes the whole card clickable, e.g. to apply a matching filter. */
  onClick?: () => void;
  /** Highlights the card, e.g. when its filter is active. */
  active?: boolean;
}

/**
 * KPI tile used at the top of list and dashboard pages: label, large value and a tinted icon tile.
 * Lay several out with `StatCardGrid`.
 */
export function StatCard({ label, value, icon, color, suffix, hint, loading = false, onClick, active = false }: StatCardProps) {
  const { token } = theme.useToken();
  const accent = color ?? token.colorPrimary;
  // Two cards share a row on phones, so trim padding and the icon tile to leave room for the label.
  const compact = useBreakpoint().sm === false;
  const tileSize = compact ? 36 : 44;

  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        height: '100%',
        borderRadius: token.borderRadiusLG,
        borderColor: active ? accent : token.colorBorderSecondary,
        cursor: onClick ? 'pointer' : undefined
      }}
      styles={{ body: { padding: compact ? token.paddingSM + 2 : `${token.paddingMD}px ${token.paddingLG}px`, height: '100%' } }}
    >
      <Flex justify="space-between" align="center" gap={token.marginSM} style={{ height: '100%' }}>
        <Flex vertical gap={token.marginXXS} style={{ minWidth: 0 }}>
          {/* Labels may take two lines so narrow cards on phones don't cut them off. */}
          <Text
            type="secondary"
            style={{
              fontSize: compact ? token.fontSizeSM : token.fontSize,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              // Break only words that can't fit at all, so normal labels wrap between words.
              overflowWrap: 'break-word'
            }}
          >
            {label}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 64, minWidth: 64 }} />
          ) : (
            <Text strong style={{ fontSize: token.fontSizeHeading3, lineHeight: 1.2, color: accent }}>
              {value}
              {suffix != null ? (
                <Text type="secondary" style={{ fontSize: token.fontSize, fontWeight: 'normal', marginInlineStart: 4 }}>
                  {suffix}
                </Text>
              ) : null}
            </Text>
          )}
          {hint != null && !loading ? (
            <Text type="secondary" ellipsis={{ tooltip: true }} style={{ fontSize: token.fontSizeSM }}>
              {hint}
            </Text>
          ) : null}
        </Flex>
        <Flex
          align="center"
          justify="center"
          style={{
            width: tileSize,
            height: tileSize,
            flexShrink: 0,
            borderRadius: token.borderRadiusLG,
            // 8-digit hex keeps the tile a light tint of the accent in both themes.
            background: tintColor(accent),
            color: accent,
            fontSize: compact ? 16 : 20
          }}
        >
          {icon}
        </Flex>
      </Flex>
    </Card>
  );
}

export default StatCard;
